import { Button, ModalV2, SearchInput } from '@components'
import { useBusinessContext } from '@context'
import { collectFromPages, fetchPISProviders } from '@query'
import { useInfiniteQuery } from '@tanstack/react-query'
import { getApiErrorMessage } from '@utils'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import styled from 'styled-components'

interface Props {
  isVisible: boolean
  onClose: () => void
  /** Return false when the failure was handled and the modal should stay open. */
  onSelectProvider: (providerCode: string) => boolean | void | Promise<boolean | void>
}

export const PISProviderSelector: React.FC<Props> = ({ isVisible, onClose, onSelectProvider }) => {
  const [t, i18n] = useTranslation()
  const { slug: businessSlug, data: business } = useBusinessContext()
  const [search, setSearch] = useState('')
  const [countryScope, setCountryScope] = useState<'current' | 'all'>('current')
  const [selectedProviderCode, setSelectedProviderCode] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const businessCountryCode = (
    business?.country ||
    business?.business_country ||
    'FI'
  ).toUpperCase()
  const currentCountryLabel = useMemo(() => {
    try {
      return (
        new Intl.DisplayNames([i18n.language], { type: 'region' }).of(businessCountryCode) ||
        businessCountryCode
      )
    } catch {
      return businessCountryCode
    }
  }, [i18n.language, businessCountryCode])
  const apiCountry = countryScope === 'current' ? businessCountryCode : 'ALL'

  const {
    data,
    isFetching: isProvidersFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useInfiniteQuery({
    queryKey: [businessSlug, 'pis_providers', apiCountry, search],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) =>
      fetchPISProviders({
        slug: businessSlug ?? '',
        country: apiCountry,
        search,
        page: pageParam
      }),
    getNextPageParam: lastPage => {
      if (!lastPage.next) {
        return undefined
      }
      try {
        const nextPage = Number(new URL(lastPage.next).searchParams.get('page'))
        return Number.isFinite(nextPage) && nextPage > 0 ? nextPage : undefined
      } catch {
        return undefined
      }
    },
    enabled: isVisible && !!businessSlug
  })

  useEffect(() => {
    if (!isVisible) {
      return
    }
    setSelectedProviderCode(null)
  }, [isVisible, apiCountry, search, businessSlug])

  const providers = collectFromPages(data)

  const handleProviderScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight
      if (distanceFromBottom <= 150 && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  )

  const handleContinue = async () => {
    if (!selectedProviderCode || isSubmitting) {
      return
    }
    setIsSubmitting(true)
    try {
      const shouldClose = await onSelectProvider(selectedProviderCode)
      if (shouldClose !== false) {
        onClose()
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('general.error')))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalV2
      isVisible={isVisible}
      header={t('components.paymentButton.pisProviderSelector.header')}
      handleOnClose={onClose}
      width={500}
      height={760}
    >
      <PISProviderSelectorBody>
        <SearchInput
          onSearch={setSearch}
          delay={250}
          autoFocus={isVisible}
          placeholder={t('components.paymentButton.pisProviderSelector.searchPlaceholder')}
        />

        <PISFilterRow>
          <PISFilterLabel>
            {t('components.paymentButton.pisProviderSelector.countries')}
          </PISFilterLabel>
          <PISFilterBadges>
            <PISFilterBadge
              type="button"
              $active={countryScope === 'current'}
              onClick={() => setCountryScope('current')}
            >
              {currentCountryLabel}
            </PISFilterBadge>
            <PISFilterBadge
              type="button"
              $active={countryScope === 'all'}
              onClick={() => setCountryScope('all')}
            >
              {t('components.paymentButton.pisProviderSelector.allCountries')}
            </PISFilterBadge>
          </PISFilterBadges>
        </PISFilterRow>

        <PISProviderSections onScroll={handleProviderScroll}>
          <PISCardGrid>
            {providers.map(provider => (
              <PISProviderCard
                key={provider.provider_code}
                type="button"
                $selected={selectedProviderCode === provider.provider_code}
                onClick={() => setSelectedProviderCode(provider.provider_code)}
              >
                <PISProviderCountry>
                  {provider.country_code?.toUpperCase() || 'N/A'}
                </PISProviderCountry>
                {provider.provider_logo_url ? (
                  <PISProviderLogo src={provider.provider_logo_url} alt="" aria-hidden="true" />
                ) : null}
                <PISProviderName>
                  {provider.provider_name || provider.provider_code}
                </PISProviderName>
              </PISProviderCard>
            ))}
          </PISCardGrid>

          {!isProvidersFetching && !providers.length && (
            <PISEmptyState>
              {t('components.paymentButton.pisProviderSelector.noProviders')}
            </PISEmptyState>
          )}
          {isFetchingNextPage && hasNextPage && !!providers.length && (
            <PISEmptyState>
              {t('components.paymentButton.pisProviderSelector.loadingMore')}
            </PISEmptyState>
          )}
        </PISProviderSections>

        <PISFooter>
          <Button type="button" intent="default" onClick={onClose}>
            {t('general.cancel')}
          </Button>
          <Button
            type="button"
            intent="success"
            onClick={handleContinue}
            disabled={!selectedProviderCode || isSubmitting}
            showSpinner={isSubmitting}
          >
            {t('components.paymentButton.pisProviderSelector.continue')}
          </Button>
        </PISFooter>
      </PISProviderSelectorBody>
    </ModalV2>
  )
}

const PISProviderSelectorBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  height: 100%;
`

const PISFilterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const PISFilterLabel = styled.span`
  font-weight: 600;
`

const PISFilterBadges = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const PISFilterBadge = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  white-space: nowrap;
  padding: ${({ theme }) => theme.spacing.xxxs}rem ${({ theme }) => theme.spacing.xs}rem;
  cursor: pointer;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.neutralGray)};
  color: ${({ $active, theme }) => ($active ? theme.colors.neutralWhite : theme.colors.metalGray)};
`

const PISProviderSections = styled.div`
  flex: 1;
  overflow: auto;
  padding-bottom: ${({ theme }) => theme.spacing.sm}rem;
`

const PISCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const PISProviderCard = styled.button<{ $selected: boolean }>`
  border: 1px solid
    ${({ $selected, theme }) => ($selected ? theme.colors.primary : theme.colors.neutralGray)};
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  min-height: 8.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  cursor: pointer;
  text-align: center;
`

const PISProviderCountry = styled.span`
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  letter-spacing: 0.03em;
`

const PISProviderLogo = styled.img`
  max-height: 2.5rem;
  max-width: 8rem;
  padding: ${({ theme }) => theme.spacing.xxs}rem;
  object-fit: contain;
`

const PISProviderName = styled.span`
  font-weight: 500;
`

const PISEmptyState = styled.div`
  color: ${({ theme }) => theme.colors.iconGray};
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg}rem 0;
`

const PISFooter = styled.div`
  display: flex;
  justify-content: space-between;
`
